import React, { useState, useEffect } from "react";
import { db, storage, auth } from "../firebase";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Autocomplete from '@mui/material/Autocomplete';

const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const languageOptions = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "zh", label: "Chinese" },
  { value: "ar", label: "Arabic" },
  { value: "ru", label: "Russian" },
  { value: "de", label: "German" },
  // Add more as needed
];

export default function GeneralInfoForm({ profile, onSaved }) {
  const [calledName, setCalledName] = useState(profile?.calledName || "");
  const [gender, setGender] = useState(profile?.gender || "");
  const [languages, setLanguages] = useState(
    Array.isArray(profile?.languages)
      ? languageOptions.filter(opt => profile.languages.includes(opt.value))
      : []
  );
  const [emailId, setEmailId] = useState(profile?.emailId || "");
  const [whatsapp, setWhatsapp] = useState(profile?.whatsapp || "");
  const [telegram, setTelegram] = useState(profile?.telegram || "");
  const [photo, setPhoto] = useState(profile?.photoURL || "");
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    setCalledName(profile?.calledName || "");
    setGender(profile?.gender || "");
    setLanguages(Array.isArray(profile?.languages) ? languageOptions.filter(opt => profile.languages.includes(opt.value)) : []);
    setEmailId(profile?.emailId || "");
    setWhatsapp(profile?.whatsapp || "");
    setTelegram(profile?.telegram || "");
    setPhoto(profile?.photoURL || "");
  }, [profile]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      setPhoto(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) return;
      let uploadedPhotoURL = photo;
      if (photoFile) {
        const photoRef = ref(storage, `avatars/${user.uid}`);
        await uploadBytes(photoRef, photoFile);
        uploadedPhotoURL = await getDownloadURL(photoRef);
        setPhoto(uploadedPhotoURL);
      }
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          calledName,
          gender,
          languages: languages.map(l => l.value),
          emailId,
          whatsapp,
          telegram,
          photoURL: uploadedPhotoURL,
        },
        { merge: true }
      );
      setMessage("Saved!");
      setShowSnackbar(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError("Error saving info");
      setShowSnackbar(true);
      console.error("Error saving general info:", err);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setMessage("");
        setError("");
        setShowSnackbar(false);
      }, 2500);
    }
  };

  return (
    <Card sx={{ maxWidth: 480, margin: '32px auto', borderRadius: 3, boxShadow: 3 }}>
      <CardHeader
        title={<Typography variant="h5" align="center">General Information</Typography>}
      />
      <CardContent>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <Avatar
                src={photo}
                sx={{ width: 96, height: 96, margin: '0 auto', fontSize: 40 }}
                alt={calledName ? `${calledName}'s profile photo` : 'Profile photo'}
              >
                {calledName ? calledName[0].toUpperCase() : (emailId ? emailId[0].toUpperCase() : '?')}
              </Avatar>
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'background.paper', boxShadow: 1 }}
                size="small"
              >
                <PhotoCamera fontSize="small" />
                <input hidden accept="image/*" type="file" onChange={handlePhotoChange} />
              </IconButton>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Called Name"
              value={calledName}
              onChange={e => setCalledName(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                value={gender}
                label="Gender"
                onChange={e => setGender(e.target.value)}
              >
                {genderOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={languageOptions}
              getOptionLabel={option => option.label}
              value={languages}
              onChange={(_, value) => setLanguages(value)}
              renderInput={params => <TextField {...params} label="Languages" placeholder="Select languages" />}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Email ID"
              type="email"
              value={emailId}
              onChange={e => setEmailId(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="WhatsApp"
              value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Telegram"
              value={telegram}
              onChange={e => setTelegram(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
              sx={{ mt: 2 }}
              onClick={handleSave}
            >
              {loading ? "Saving..." : "Save General Info"}
            </Button>
          </Grid>
        </Grid>
      </CardContent>
      <Snackbar open={showSnackbar} autoHideDuration={2500} onClose={() => setShowSnackbar(false)}>
        {message ? (
          <Alert severity="success" sx={{ width: '100%' }}>{message}</Alert>
        ) : error ? (
          <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
        ) : null}
      </Snackbar>
    </Card>
  );
}
